/**
 * RunRail — activity log for Fusion Canvas.
 *
 * Every evidence card, radial-menu operator, and Autopilot act posts a job chip
 * here. Status is honest (queued / running / done / error). Click a chip to
 * center and select the node that run touched. This is not the ingestion
 * timeline (that is Play on the canvas) and not Workbench fund-flow playback.
 */
import { Loader2, CheckCircle2, AlertTriangle, Clock, X, Crosshair } from 'lucide-react';
import { useFusionStore, type CanvasJob } from '../../stores/useFusionStore';
import { evidenceById, operatorById } from '../../case-canvas/registry';
import type cytoscape from 'cytoscape';

function statusMeta(status: CanvasJob['status']) {
  if (status === 'running') {
    return { icon: <Loader2 size={12} className="animate-spin text-sky-600" />, label: 'Running', tone: 'text-sky-700 bg-sky-500/10 border-sky-500/30' };
  }
  if (status === 'done') {
    return { icon: <CheckCircle2 size={12} className="text-emerald-600" />, label: 'Done', tone: 'text-emerald-700 bg-emerald-500/10 border-emerald-500/30' };
  }
  if (status === 'error') {
    return { icon: <AlertTriangle size={12} className="text-red-600" />, label: 'Error', tone: 'text-red-700 bg-red-500/10 border-red-500/30' };
  }
  return { icon: <Clock size={12} className="text-muted-foreground" />, label: 'Queued', tone: 'text-muted-foreground bg-muted border-border' };
}

/** Prefer human operator/evidence name; fall back to registry id. */
function jobTitle(job: CanvasJob): string {
  const op = operatorById(job.operator);
  if (op) return op.name;
  const ev = evidenceById(job.operator);
  if (ev) return ev.name;
  // Playbook synthetic chips sometimes put the name in label / operator text.
  const fromLabel = job.label.split('·')[0]?.trim();
  if (fromLabel && !/^O\d|^E\d|^A\d/i.test(fromLabel)) return fromLabel;
  return job.operator;
}

function jobDetail(job: CanvasJob): string {
  const title = jobTitle(job);
  // Strip duplicated title prefix from "UTR-Stitch · account:50100"
  const raw = job.label.trim();
  if (raw.toLowerCase().startsWith(title.toLowerCase())) {
    const rest = raw.slice(title.length).replace(/^[·•\-\s]+/, '').trim();
    return rest || job.message || '';
  }
  return raw || job.message || '';
}

export function RunRail() {
  const jobs = useFusionStore((s) => s.jobs);
  const clearJobs = useFusionStore((s) => s.clearJobs);
  const select = useFusionStore((s) => s.select);

  const focus = (nodeId?: string) => {
    if (!nodeId) return;
    const cy = (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
    const n = cy?.getElementById(nodeId);
    if (cy && n && n.length) {
      cy.animate({ center: { eles: n } }, { duration: 220 });
      select({ id: nodeId, kind: 'node' });
    }
  };

  if (jobs.length === 0) return null;

  return (
    <div
      className="min-h-14 shrink-0 border-t border-border bg-card/80 flex items-center gap-3 px-3 py-1.5 overflow-x-auto"
      data-tour="fusion-run-rail"
      role="region"
      aria-label="Run rail — job activity"
    >
      <div className="shrink-0 min-w-[7.5rem]">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold leading-none">
          Run rail
        </div>
        <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
          Operator & evidence jobs
        </div>
      </div>

      <div className="flex items-center gap-1.5 min-w-0">
        {jobs.map((j) => {
          const meta = statusMeta(j.status);
          const title = jobTitle(j);
          const detail = jobDetail(j);
          const canFocus = Boolean(j.targetId);
          return (
            <button
              key={j.id}
              type="button"
              onClick={() => focus(j.targetId)}
              disabled={!canFocus}
              title={
                canFocus
                  ? `${title}${detail ? ` — ${detail}` : ''}${j.message ? `\n${j.message}` : ''}\nClick to focus on canvas`
                  : `${title}${detail ? ` — ${detail}` : ''}${j.message ? `\n${j.message}` : ''}`
              }
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border bg-secondary/40 transition-colors text-xs whitespace-nowrap max-w-[18rem] ${
                canFocus
                  ? 'hover:bg-secondary cursor-pointer border-border'
                  : 'opacity-80 cursor-default border-border/70'
              }`}
            >
              {meta.icon}
              <span className={`px-1 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wide ${meta.tone}`}>
                {meta.label}
              </span>
              <span className="font-medium truncate">{title}</span>
              {detail && <span className="text-muted-foreground truncate">{detail}</span>}
              {canFocus && <Crosshair className="w-3 h-3 text-primary/70 shrink-0" aria-hidden />}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={clearJobs}
        className="ml-auto shrink-0 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2.5 py-1.5 rounded-md border border-border bg-secondary hover:bg-secondary/80 transition-colors"
      >
        <X className="w-3.5 h-3.5" /> Clear
      </button>
    </div>
  );
}
